import React, {useEffect, useMemo, useState} from 'react'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import MenuIcon from '@mui/icons-material/Menu'
import {getToken, getMerchant, isAuthorized, isEmailDomainAllowed} from '../helpers/ReduxHelpers'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import {Button, ListItemIcon, Typography, Divider, Tooltip} from '@mui/material'
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined'
import Logout from '@mui/icons-material/Logout'
import {performLogout} from '../helpers/sessionHelper'
import Settings from '@mui/icons-material/Settings'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'

import PopupState, {bindTrigger, bindMenu} from 'material-ui-popup-state'
import PasswordIcon from '@mui/icons-material/Password'
import MarqueeNotification from '../common/BannerNotification'
import {usePageTitle} from '../common/PageTitleContext'
import {apiConstants, postGeneric} from '../common/constants'
import {decodeAuthToken} from '../helpers/UtilityHelper'
import {switchAccount} from '../redux/userSlice'
import ConfirmDialog from '../common/ConfirmDialog'
import WeightDiscrepancyAlert from './WdInfo'
import {useSidebarToggle} from '../components/SidebarToggleContext'
import WalletDetails from './WalletDetails'
import NotificationDetails from './NotificationDetails'
import ReleaseNotesBell from './ReleaseNotesBell'
import {ChangeCircleTwoTone} from '@mui/icons-material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import {alpha} from '@mui/material/styles'
import {ROLE_COMBINATIONS} from '../../routes/routes.config'
import useMerchantSwitcher from '../hooks/useMerchantSwitcher'
import {SUPPORT_PATH} from '../../routes/emsV2Visibility'

// Clean single-row heading: a heavier/tighter title over a muted commentary line that wraps
// to at most two lines (then ellipsis), so long, dynamic copy never crowds the actions.
const titleSx = {fontSize: '1.02rem', fontWeight: 700, letterSpacing: '-0.01em', color: 'text.primary', lineHeight: 1.18, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}
const subSx = {fontSize: '0.72rem', color: 'text.disabled', lineHeight: 1.45, maxWidth: 640, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}

// On phones (xs) the actions group is flattened into the toolbar (`display: contents`) so its
// items can be re-ordered into two compact rows: [menu | title | Ask AI | Support] and
// [wallet ........ alerts | merchant name]. From sm up nothing here applies and the layout is
// exactly as before.
const PHONE_ROW_BREAK = {display: {xs: 'block', sm: 'none'}, flexBasis: '100%', height: 0, order: 5}
const iconOnlyOnPhone = {
  '& .MuiButton-startIcon': {mr: {xs: 0, sm: 1}, ml: {xs: 0, sm: '-4px'}},
  minWidth: {xs: 36, sm: 64},
  px: {xs: 0, sm: 1.4},
}
const labelHiddenOnPhone = {display: {xs: 'none', sm: 'inline'}}

const menuPaperProps = {
  sx: {
    borderRadius: 1.5,
    minWidth: 200,
    '& li': {
      px: 2,
      py: 1,
      typography: 'body2',
    },
    p: 0,
    overflow: 'visible',
    boxShadow: (theme) => theme.customShadows.z20,
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      right: 14,
      width: 10,
      height: 10,
      bgcolor: 'background.paper',
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 0,
      borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
      borderTop: (theme) => `1px solid ${theme.palette.divider}`,
    },
  },
}

export default function TopBar(props) {
  const merchantD = getMerchant()
  const merchantId = merchantD?.id ? merchantD?.id : ''
  const [notificationList, setNotificationList] = useState([])
  const [switchConfirm, setSwitchConfirm] = useState(false)
  const dispatch = useDispatch()
  const {
    user: {userInfo},
  } = useSelector((state) => state)
  let navigate = useNavigate()
  const accessToken = getToken()
  const {merchantList, roles} = decodeAuthToken(accessToken)
  const {showSwitcher: showMerchantSwitcher} = useMerchantSwitcher()
  const {sidebarWidth, setMobileOpen} = useSidebarToggle()
  const {pageTitle, pageSubTitle} = usePageTitle()

  const onLogOut = () => {
    performLogout()
  }

  useEffect(() => {
    window?.FreshworksWidget('hide', 'launcher')
  }, [])

  useEffect(() => {
    postGeneric(`${apiConstants.GET_NOTIFICATIONS}`, {
      andfilter: [
        {
          field: 'active',
          operator: 'eq',
          value: true,
        },
      ],
      limit: 15,
    })
      .then((res) => {
        setNotificationList(res?.data?.data)
      })
      .catch((err) => {
        console.log(err)
      })
  }, [])

  // banner notification calculation, extract notification based on date range
  const bannerNotifications = useMemo(() => {
    const today = new Date()
    return notificationList?.filter((val) => {
      if (val.destination !== 'PUSH') {
        const parseDate = (dateStr) => {
          const [day, month, year, time] = dateStr.split(/[- ]/)
          return new Date(`${year}-${month}-${day}T${time || '00:00:00'}`) // Ensures ISO format
        }
        const startDate = parseDate(val.startDate)
        const endDate = parseDate(val.endDate)
        return today >= startDate && today <= endDate
      }
      return false
    })
  }, [notificationList])

  const onSwitchAccount = () => {
    navigate('/loginuser')
    dispatch(switchAccount())
  }

  return (
    <>
      <ConfirmDialog
        title=''
        open={switchConfirm}
        setOpen={setSwitchConfirm}
        onConfirm={() => onSwitchAccount()}
      >
        Are you sure you want to switch accounts?
      </ConfirmDialog>

      <Box
        sx={{
          flexShrink: 0,
          ml: {
            md: sidebarWidth ? '68px' : '256px',
          },
          transition: 'margin-left 0.2s ease-in-out',
          paddingTop: bannerNotifications?.length > 0 ? '40px' : '0px',
        }}
      >
        {bannerNotifications?.length > 0 && (
          <MarqueeNotification notificationList={bannerNotifications} />
        )}

        <AppBar
          position='static'
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 0,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Toolbar sx={{flexWrap: {xs: 'wrap', md: 'nowrap'}}}>
            <IconButton
              size='large'
              edge='start'
              color='inherit'
              aria-label='Open menu'
              onClick={() => setMobileOpen(true)}
              sx={{
                display: {
                  md: 'none',
                },
                mr: {xs: 0.5, sm: 2},
                order: {xs: 0, sm: 0},
              }}
            >
              <MenuIcon color='primary' />
            </IconButton>
            {/* Title + commentary inline; commentary wraps to max 2 lines then ellipsis.
                pr keeps a clear gutter so text never runs up against the action icons. */}
            <Box sx={{flex: 1, minWidth: 0, pr: {xs: 1, md: 5}, order: {xs: 1, sm: 0}}}>
              <Typography sx={titleSx}>{pageTitle}</Typography>
              {pageSubTitle && <Typography sx={{...subSx, mt: '2px', WebkitLineClamp: {xs: 1, sm: 2}}}>{pageSubTitle}</Typography>}
            </Box>
            {/* Below tablet width this group drops to its own row under the title. */}
            <Box sx={PHONE_ROW_BREAK} />
            <Box sx={{
              display: {xs: 'contents', sm: 'flex'}, alignItems: 'center', gap: 0.5,
              flexWrap: 'wrap', width: {sm: '100%', md: 'auto'},
              justifyContent: {sm: 'flex-end', md: 'normal'}, pb: {sm: 1, md: 0},
            }}>
              <Box sx={{order: {xs: 6, sm: 0}, display: 'flex', alignItems: 'center'}}>
                <WalletDetails merchantId={merchantId} />
              </Box>

              {/* Utility icons grouped into a single tray (matches the mockup) */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.25,
                p: '3px', border: 1, borderColor: 'divider', borderRadius: '12px',
                bgcolor: 'action.hover', order: {xs: 7, sm: 0}, ml: {xs: 'auto', sm: 0},
              }}>
                <WeightDiscrepancyAlert merchantId={merchantId} />
                <NotificationDetails notificationList={notificationList} />
                <ReleaseNotesBell />
              </Box>

              {/* Ask AI — indigo→purple gradient pill */}
              {isEmailDomainAllowed(process.env.REACT_APP_PROPILOT_ALLOWED_DOMAINS?.split(',')) && (
                <Button size='small' onClick={() => navigate('/proship-pilot')}
                  startIcon={<AutoAwesomeOutlinedIcon sx={{fontSize: 16}} />}
                  sx={{
                    background: 'linear-gradient(135deg, #7c4dff, #3783e7)',
                    color: '#fff', borderRadius: '16px', px: 1.5, py: 0.4,
                    fontSize: '11px', fontWeight: 600, textTransform: 'none',
                    fontFamily: 'Poppins, sans-serif', ml: {xs: 0, sm: 0.5},
                    order: {xs: 2, sm: 0}, minWidth: {xs: 34, sm: 64}, minHeight: {xs: 34, sm: 0},
                    '& .MuiButton-startIcon': {mr: {xs: 0, sm: 1}, ml: {xs: 0, sm: '-4px'}},
                    px: {xs: 0, sm: 1.5},
                    '&:hover': {background: 'linear-gradient(135deg, #651fff, #2962ff)'},
                  }}
                  aria-label='Ask AI'>
                  <Box component='span' sx={labelHiddenOnPhone}>Ask AI</Box>
                </Button>
              )}

              {/* Help — standalone teal support chip, bracketed by dividers */}
              {isAuthorized(ROLE_COMBINATIONS.TOOLS_ACCESS) && (
                <>
                  <Divider orientation='vertical' flexItem sx={{mx: 0.5, my: 1.2, borderColor: 'divider', display: {xs: 'none', sm: 'block'}}} />
                  <Tooltip title='Raise a support ticket'>
                    <Button size='small' onClick={() => navigate(SUPPORT_PATH)}
                      startIcon={<HeadsetMicOutlinedIcon sx={{fontSize: 16}} />}
                      sx={{
                        color: '#0f9d8f', bgcolor: alpha('#0f9d8f', 0.12), borderRadius: '10px',
                        minHeight: 36, fontSize: '12.5px', fontWeight: 600, textTransform: 'none',
                        order: {xs: 3, sm: 0},
                        ...iconOnlyOnPhone,
                        '&:hover': {bgcolor: alpha('#0f9d8f', 0.2)},
                      }}
                      aria-label='Support'>
                      <Box component='span' sx={labelHiddenOnPhone}>Support</Box>
                    </Button>
                  </Tooltip>
                </>
              )}

              <Divider orientation='vertical' flexItem sx={{mx: 0.5, my: 1.2, borderColor: 'divider', display: {xs: 'none', sm: 'block'}}} />
              <PopupState variant='popover' popupId='profile-menu'>
                {(popupState) => (
                  <React.Fragment>
                    <Button
                      {...bindTrigger(popupState)}
                      variant='text'
                      disableElevation
                      endIcon={<KeyboardArrowDownIcon sx={{fontSize: 18, color: 'text.secondary'}} />}
                      sx={{
                        boxShadow: 'none',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        typography: 'caption',
                        fontWeight: 'fontWeightMedium',
                        color: 'text.primary',
                        order: {xs: 8, sm: 0},
                        minWidth: {xs: 0, sm: 64},
                        '& .MuiButton-startIcon': {mr: {xs: 0.5, sm: 1}, ml: {xs: 0, sm: '-4px'}},
                        '& .MuiButton-endIcon': {ml: {xs: 0, sm: 1}},
                        '&:hover': {bgcolor: 'action.hover'},
                      }}
                      startIcon={
                        userInfo?.merchant?.logo ? (
                          <Box component='img' src={userInfo.merchant.logo} alt='' sx={{
                            maxHeight: 28, maxWidth: {xs: 44, sm: 80}, objectFit: 'contain', borderRadius: '4px',
                          }} />
                        ) : (
                          <AccountCircleOutlinedIcon sx={{fontSize: 26, color: 'text.secondary'}} />
                        )
                      }
                    >
                      <Box component='span' sx={{
                        display: 'inline-block', verticalAlign: 'middle',
                        maxWidth: {xs: 60, md: 'none'}, overflow: 'hidden', // full name is at the top of the menu
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        <Box component='span' sx={{display: {xs: 'none', sm: 'inline'}}}>Welcome, </Box>
                        {userInfo?.merchant?.name || 'Team'}
                      </Box>
                    </Button>
                    <Menu
                      {...bindMenu(popupState)}
                      keepMounted
                      PaperProps={menuPaperProps}
                      transformOrigin={{horizontal: 'right', vertical: 'top'}}
                      anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    >
                      <MenuItem
                        dense
                        divider
                        sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, py: 1.5}}
                      >
                        <Typography variant='subtitle2'>{userInfo?.merchant?.name}</Typography>
                        <Typography variant='caption' color='text.secondary'>{userInfo?.username}</Typography>
                      </MenuItem>
                      {showMerchantSwitcher && (
                        <MenuItem dense divider onClick={() => { navigate('/loginuser'); dispatch(switchAccount()) }}>
                          <ListItemIcon>
                            <ChangeCircleTwoTone fontSize='small' />
                          </ListItemIcon>
                          Switch Merchant Account
                        </MenuItem>
                      )}
                      {roles?.includes('ROLE_PROSHIPADMIN') &&
                        (merchantList === undefined ||
                          merchantList?.length === 0 ||
                          merchantList?.length > 0) && (
                          <MenuItem dense divider onClick={() => setSwitchConfirm(true)}>
                            <ListItemIcon>
                              <ChangeCircleTwoTone fontSize='small' />
                            </ListItemIcon>
                            Switch Account
                          </MenuItem>
                        )}

                      {isAuthorized(ROLE_COMBINATIONS.SETTINGS_ACCESS) && (
                        <MenuItem dense onClick={() => navigate('/setting')}>
                          <ListItemIcon>
                            <Settings fontSize='small' />
                          </ListItemIcon>
                          Settings
                        </MenuItem>
                      )}
                      <MenuItem dense onClick={() => navigate('/change-password')}>
                        <ListItemIcon>
                          <PasswordIcon color='warning' fontSize='small' />
                        </ListItemIcon>
                        Change Password
                      </MenuItem>
                      <MenuItem dense onClick={onLogOut}>
                        <ListItemIcon>
                          <Logout fontSize='small' color='error' />
                        </ListItemIcon>
                        Logout
                      </MenuItem>
                    </Menu>
                  </React.Fragment>
                )}
              </PopupState>
            </Box>
          </Toolbar>
        </AppBar>
      </Box>
    </>
  )
}
